import React, { useState } from 'react';
import explanationsData from '../jsons/explanations.json';
import ExplanationRenderer from './ExplanationRenderer';
import ExampleRenderer from './ExampleRenderer';
import { usePremium } from '../contexts/PremiumContext';

const Explanations = ({ onShowPayment }) => {
  const { isPremium: userHasPremium, getPremiumStatusInfo } = usePremium();
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [openToggles, setOpenToggles] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedTooltip, setSelectedTooltip] = useState(null);

  // Get all chapter names from the JSON (excluding metadata)
  const chapters = Object.keys(explanationsData).filter(key => key !== 'metadata');

  // Protection helper functions
  const isChapterProtected = React.useCallback((chapterName) => {
    return explanationsData.metadata && 
           explanationsData.metadata[chapterName] && 
           explanationsData.metadata[chapterName].protected === true;
  }, []);

  const canUserAccessChapter = React.useCallback((chapterName) => {
    return !isChapterProtected(chapterName) || userHasPremium;
  }, [isChapterProtected, userHasPremium]);

  const getChapterMetadata = React.useCallback((chapterName) => {
    return explanationsData.metadata && explanationsData.metadata[chapterName] 
      ? explanationsData.metadata[chapterName] 
      : { title: chapterName, protected: false, description: '', price: null };
  }, []);

  // Initialize with the first chapter if none selected
  React.useEffect(() => {
    if (chapters.length > 0 && !selectedChapter) {
      setSelectedChapter(chapters[0]);
    }
  }, [chapters, selectedChapter]);

  // Handle keyboard events for modal
  React.useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Escape' && selectedTooltip) {
        closeTooltipModal();
      }
    };

    if (selectedTooltip) {
      document.addEventListener('keydown', handleKeyPress);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset';
    };
  }, [selectedTooltip]);

  const toggleConcept = (conceptName) => {
    setOpenToggles(prev => ({
      ...prev,
      [conceptName]: !prev[conceptName]
    }));
  };

  const openTooltipModal = (term, definition) => {
    setSelectedTooltip({ term, definition });
  };

  const closeTooltipModal = () => {
    setSelectedTooltip(null);
  };

  const handleChapterSelect = (chapter) => {
    // Check if the chapter is protected and user doesn't have premium access
    if (isChapterProtected(chapter) && !userHasPremium) {
      console.log('Protected chapter accessed:', chapter, 'onShowPayment available:', !!onShowPayment);
      if (onShowPayment) {
        onShowPayment();
      }
      return;
    }

    // User can access this chapter
    setSelectedChapter(chapter);
    // Clear all open toggles when switching chapters
    setOpenToggles({});
    // Close sidebar on mobile after selection
    setSidebarOpen(false);
    // Close any open tooltip modal
    setSelectedTooltip(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Mobile menu button */}
      <div className="lg:hidden bg-white border-b border-gray-200 p-4">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <span className="font-medium">Chapters</span>
          {selectedChapter && (
            <span className="ml-2 text-sm text-gray-500">- {selectedChapter}</span>
          )}
        </button>
      </div>

      <div className="flex flex-col lg:flex-row relative">
        {/* Sidebar */}
        <div className={`${
          sidebarOpen ? 'block' : 'hidden'
        } lg:block w-full lg:w-80 bg-white shadow-xl border-r border-gray-200 lg:min-h-screen absolute lg:relative z-10 lg:z-auto`}>
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <h1 className="text-2xl font-bold text-white">Explanations</h1>
                  {userHasPremium && (
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-semibold" title={getPremiumStatusInfo().message}>
                      ✨ Premium
                    </div>
                  )}
                </div>
                <p className="text-blue-100 text-sm">
                  စတင် လေ့လာရန် အခန်းတစ်ခန်းကို ရွေးပါ။
                  {!userHasPremium && (
                    <span className="block text-blue-200 text-xs mt-1">
                      💡 Premium chapters require subscription
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-white hover:text-blue-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          <nav className="p-4 max-h-screen lg:max-h-none overflow-y-auto">
            <div className="space-y-2">
              {chapters.map((chapter, index) => {
                const isProtected = isChapterProtected(chapter);
                const canAccess = canUserAccessChapter(chapter);
                const chapterMetadata = getChapterMetadata(chapter);

                return (
                  <button
                    key={index}
                    onClick={() => handleChapterSelect(chapter)}
                    className={`w-full text-left p-4 rounded-lg transition-all duration-200 border relative ${
                      isProtected && !canAccess
                        ? 'border-orange-200 bg-gradient-to-br from-gray-50 to-orange-50 hover:border-orange-300'
                        : selectedChapter === chapter
                          ? 'bg-blue-50 border-blue-200 text-blue-800 shadow-md transform scale-105'
                          : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300'
                    }`}
                  >
                    {/* Lock icon for protected content */}
                    {isProtected && !canAccess && (
                      <div className="absolute top-2 right-2">
                        <div className="bg-orange-100 p-1 rounded-full">
                          <svg className="w-3 h-3 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center">
                      <span className="text-lg mr-3">
                        {isProtected && !canAccess 
                          ? '🔒' 
                          : selectedChapter === chapter 
                            ? '📖' 
                            : '📄'
                        }
                      </span>
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm">{chapterMetadata.title || chapter}</h3>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-gray-500">
                            {explanationsData[chapter].length} concepts
                          </p>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            isProtected 
                              ? 'bg-orange-100 text-orange-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {isProtected ? '💎 Premium' : '📝 Free'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </nav>
        </div>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-5"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}

        {/* Main Content */}
        <div className="flex-1 p-4 lg:p-8">
          {selectedChapter ? (
            <div>
              {/* Check if user can access this chapter */}
              {!canUserAccessChapter(selectedChapter) ? (
                <div className="text-center py-16">
                  <div className="bg-white rounded-xl shadow-lg border border-orange-200 p-8 max-w-md mx-auto">
                    <div className="text-6xl mb-4">🔒</div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Premium Content</h3>
                    <p className="text-gray-600 mb-4">
                      This chapter requires a premium subscription to access.
                    </p>
                    <button
                      onClick={() => onShowPayment && onShowPayment()}
                      className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium py-3 px-6 rounded-lg transition-all transform hover:scale-105"
                    >
                      🔓 Upgrade to Premium
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="mb-8">
                    <h2 className="text-3xl font-bold text-gray-800 mb-2">{selectedChapter}</h2>
                    <div className="h-1 w-24 bg-gradient-to-r from-blue-500 to-indigo-500 rounded"></div>
                  </div>

              <div className="space-y-6">
                {explanationsData[selectedChapter].map((concept, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow duration-300"
                  >
                    {/* Concept Toggle Header */}
                    <button
                      onClick={() => toggleConcept(concept.concept_name)}
                      className="w-full p-6 text-left hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="text-2xl mr-4">
                            {openToggles[concept.concept_name] ? '📖' : '📚'}
                          </span>
                          <h3 className="text-xl font-semibold text-gray-800">
                            {concept.concept_name}
                          </h3>
                        </div>
                        <div className={`transform transition-transform duration-200 ${
                          openToggles[concept.concept_name] ? 'rotate-180' : ''
                        }`}>
                          <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </button>

                    {/* Expandable Content */}
                    {openToggles[concept.concept_name] && (
                      <div className="border-t border-gray-200 bg-gray-50 animate-fade-in">
                        {/* Tooltips Section */}
                        {concept.tooltips && Object.keys(concept.tooltips).length > 0 && (
                          <div className="p-6 border-b border-gray-200">
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(concept.tooltips).map(([term, definition], tooltipIndex) => (
                                <button
                                  key={tooltipIndex}
                                  onClick={() => openTooltipModal(term, definition)}
                                  className="inline-flex items-center px-3 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 font-medium rounded-full text-sm transition-colors duration-200 border border-amber-300 hover:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
                                >
                                  <span className="mr-2">💭</span>
                                  {term}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Detailed Explanation */}
                        <div className="p-6">
                          <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <span className="mr-2">💡</span>
                            Detailed Explanation
                          </h4>
                          <div className="bg-white p-4 rounded-lg border-l-4 border-blue-500 shadow-sm">
                            <div className="prose prose-sm max-w-none">
                              <ExplanationRenderer content={concept.detailed_explanation} />
                            </div>
                          </div>
                        </div>

                        {/* Recommended Videos */}
                        {concept.recommended_videos && concept.recommended_videos.length > 0 && (
                          <div className="px-6 pb-6">
                            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                              <span className="mr-2">🎥</span>
                              Recommended Videos ({concept.recommended_videos.length})
                            </h4>
                            <div className="space-y-6">
                              {concept.recommended_videos.map((video, videoIndex) => {
                                // Extract video ID from YouTube URL
                                const getYouTubeVideoId = (url) => {
                                  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
                                  const match = url.match(regExp);
                                  return (match && match[2].length === 11) ? match[2] : null;
                                };
                                
                                const videoId = getYouTubeVideoId(video.url);
                                const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : null;
                                
                                return (
                                  <div
                                    key={videoIndex}
                                    className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
                                  >
                                    <div className="mb-4">
                                      <h5 className="font-semibold text-gray-800 mb-1">{video.title}</h5>
                                      <p className="text-sm text-gray-600">{video.channel}</p>
                                    </div>
                                    
                                    {embedUrl ? (
                                      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                        <iframe
                                          className="absolute top-0 left-0 w-full h-full rounded-lg"
                                          src={embedUrl}
                                          title={video.title}
                                          frameBorder="0"
                                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                          allowFullScreen
                                        ></iframe>
                                      </div>
                                    ) : (
                                      <div className="bg-gray-100 p-4 rounded-lg text-center">
                                        <p className="text-gray-600 mb-3">Unable to embed video</p>
                                        <a
                                          href={video.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                        >
                                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                          </svg>
                                          Watch on YouTube
                                        </a>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Examples */}
                        {concept.examples && concept.examples.length > 0 && (
                          <div className="px-6 pb-6">
                            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                              <span className="mr-2">📝</span>
                              Examples ({concept.examples.length})
                            </h4>
                            <div className="space-y-4">
                              {concept.examples.map((example, exampleIndex) => (
                                <div
                                  key={exampleIndex}
                                  className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
                                  style={{ animationDelay: `${exampleIndex * 100}ms` }}
                                >
                                  <div className="flex items-center mb-3">
                                    <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-800 text-sm font-medium rounded-full mr-3">
                                      {exampleIndex + 1}
                                    </span>
                                    <h5 className="font-medium text-gray-800">Example {exampleIndex + 1}</h5>
                                  </div>
                                  <div className="ml-9">
                                    <div className="prose prose-sm max-w-none">
                                      <ExampleRenderer content={example} />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                                </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="text-6xl mb-4">📚</div>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">Select a Chapter</h3>
                <p className="text-gray-500">Choose a chapter from the sidebar to explore concepts</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tooltip Modal */}
      {selectedTooltip && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in"
          onClick={closeTooltipModal}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden transform transition-all duration-300 scale-100 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">💭</span>
                  <h3 className="text-xl font-bold">{selectedTooltip.term}</h3>
                </div>
                <button
                  onClick={closeTooltipModal}
                  className="text-white hover:text-gray-200 transition-colors duration-200 p-1"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="prose prose-sm max-w-none">
                <ExplanationRenderer content={selectedTooltip.definition} />
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <button
                onClick={closeTooltipModal}
                className="w-full sm:w-auto px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Explanations;
